import { ReactElement, ReactNode } from 'react';
import cn from 'classnames';
import { CaretDownIcon, CaretUpIcon } from '@phosphor-icons/react';

import styles from './ExpandButton.module.scss';

export interface ExpandButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  expanded?: boolean;
  children?: ReactNode;
  className?: string;
  'data-testid'?: string;
}

/**
 * ExpandButton component for toggling expanded/collapsed content views
 * Uses composition pattern - parent controls the label content via children.
 *
 * @param expanded - Boolean indicating if content is expanded (default: false)
 * @param onClick - Callback function when button is clicked
 * @param children - Button label content (parent decides what to show based on expanded state)
 * @param className - Optional additional CSS classes
 * @param data-testid - Optional test identifier
 *
 * @example
 * <ExpandButton expanded={isOpen} onClick={toggle}>
 *   {isOpen ? 'View less' : 'View all 10 images'}
 * </ExpandButton>
 */
const ExpandButton = ({
  expanded = false,
  onClick,
  children,
  className,
  'data-testid': dataTestId,
  ...restProps
}: ExpandButtonProps): ReactElement => {
  const IconComponent = expanded ? CaretUpIcon : CaretDownIcon;

  return (
    <button
      type="button"
      className={cn(styles.expandButton, className)}
      onClick={onClick}
      aria-expanded={expanded}
      data-testid={dataTestId}
      {...restProps}
    >
      <span className={styles.label}>{children}</span>
      <IconComponent
        size={20}
        weight="regular"
        className={styles.icon}
        aria-hidden="true"
      />
    </button>
  );
};

export default ExpandButton;
